import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import Fab from '../Fab';

// POA (Power of Attorney) — SLF Property. One form (1–5 clients) → grouped by (last name, address)
// into N POA reports. Bilingual (EN + TH) fields with a Translate button. The New-POA form and the
// per-POA editor share the same tabbed field components so they look identical.

type Bi = { en?: string; th?: string };
const TXN_TYPES = [
  { key: 'buy_condo', label: 'Buy Condo' },
  { key: 'lease_land', label: 'Lease Land' },
];
const isLease = (t: string) => t.startsWith('lease');
const objOf = (t: string) => t.split('_')[1]; // land | condo | house
const STATUS_LABEL: Record<string, string> = { new: 'New', pending: 'Pending', confirmed: 'Confirmed', sent: 'Sent to client' };

const DEFAULT_DONEE = {
  name: { en: 'Ac.sub Lt Ploypairin Metaya', th: 'ว่าที่ ร.ต.หญิง พลอยไพรินทร์ เมตาร์ญาร์' },
  age: '35', idNo: '1 1018 00389 66 1',
  addr: { en: '72/42 Moo 1, Taling Ngam Sub-district, Koh Samui District, Surat Thani Province', th: '72/42 หมู่ที่ 1 ตำบลตลิ่งงาม อำเภอเกาะสมุย จังหวัดสุราษฎร์ธานี' },
};
const DEFAULT_WITNESSES = [
  { name: { en: 'Miss Chaweewan Kanurak', th: 'นางสาวฉวีวรรณ กานุรักษ์' } },
  { name: { en: 'Mr. Saran Sriruengmetta', th: 'นาย ศรัณย์ ศรีเรืองเมตตา' } },
];
const DEFAULT_MADE_AT = { en: '86/11 Moo 5 Bophut, Koh Samui, Surat Thani', th: '86/11 ม.5 ต.บ่อผุด อ.เกาะสมุย จ.สุราษฎร์ธานี' };
// The three reviewers are pre-filled by default (same as the Due Diligence form), editable per POA.
const defaultReview = () => ({ compiled: { by: 'Pariya Srinaen', ok: true }, thai: { by: 'Ploypairin Metaya', ok: true }, english: { by: 'Calem Brooks', ok: true } });
// Today in YYYY-MM-DD (LOCAL date — not UTC, so it doesn't roll a day near midnight in Thailand).
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const emptyClient = () => ({ title: '', first: { en: '', th: '' }, last: { en: '', th: '' }, age: '', nationality: '', id_number: '', email: '', id_file_ids: [] as string[], addr: { line1: { en: '', th: '' }, line2: { en: '', th: '' }, city: { en: '', th: '' }, postal: '', country: { en: '', th: '' } } });

// True if any nested string/array in the value carries user input — used to warn before discarding
// a half-filled New-POA draft (the create form is local state until "generate POAs").
const anyFilled = (v: any): boolean => {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.some(anyFilled);
  if (typeof v === 'object') return Object.values(v).some(anyFilled);
  return false;
};

const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 };
const lbl: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13.5, background: 'var(--bg-app)', color: 'var(--text-primary)', boxSizing: 'border-box' };
const btnP: React.CSSProperties = { padding: '8px 18px', fontSize: 13.5, fontWeight: 700, border: 'none', borderRadius: 8, background: 'var(--accent)', color: '#fff', cursor: 'pointer' };
const btnG: React.CSSProperties = { padding: '8px 16px', fontSize: 13.5, fontWeight: 700, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer' };
const hdr: React.CSSProperties = { fontWeight: 700, color: 'var(--accent)', marginTop: 6 };
const REVIEW: [string, string][] = [['compiled', 'Report Compiled By'], ['thai', 'Thai Content Checked By'], ['english', 'English Content Checked By']];

// Merge extracted data into the form: a NON-EMPTY extracted value OVERWRITES the current one — the
// document is authoritative, so pasting it can CORRECT a wrong value (e.g. an age of 411 → 41) on the
// edit page. An EMPTY extracted value keeps whatever is already there, so a 2nd document that lacks a
// field never wipes it. Bilingual {en,th} sides are handled independently.
function mergeFill(target: any, src: any): any {
  if (src == null) return target;
  if (typeof src === 'string') return String(src).trim() !== '' ? src : target;
  if (typeof src === 'object') {
    const t = (target && typeof target === 'object') ? { ...target } : {};
    for (const k of Object.keys(src)) t[k] = mergeFill(t[k], src[k]);
    return t;
  }
  return target ?? src;
}

// Deep-set a dotted path into a cloned object (arrays preserved).
function deepSet(obj: any, path: string, val: any): any {
  const n = JSON.parse(JSON.stringify(obj || {}));
  const ks = path.split('.'); let o = n;
  for (let i = 0; i < ks.length - 1; i++) { o[ks[i]] = o[ks[i]] ?? {}; o = o[ks[i]]; }
  o[ks[ks.length - 1]] = val; return n;
}

// ── Bilingual input: EN box + one Translate button + TH box (fills the empty side) ───────────────
function BiField({ label, value, onChange, ph, kind }: { label?: string; value: Bi; onChange: (v: Bi) => void; ph?: string; kind?: 'name' | 'address' | 'text' }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const v = value || {};
  const translate = async () => {
    const en = (v.en || '').trim(), th = (v.th || '').trim();
    let from: 'en' | 'th', to: 'en' | 'th', src: string;
    if (en && !th) { from = 'en'; to = 'th'; src = en; }
    else if (th && !en) { from = 'th'; to = 'en'; src = th; }
    else if (en) { from = 'en'; to = 'th'; src = en; }
    else return;
    setBusy(true); setErr('');
    try {
      const r: any = await api.poaTranslate(src, from, to, kind || 'text');
      if (r?.text && r.text.trim()) onChange({ ...v, [to]: r.text });
      else setErr('No result — click ⇄ to retry');
    } catch (e: any) { setErr(e?.message || 'Translate failed — click ⇄ to retry'); }
    finally { setBusy(false); }
  };
  return (
    <div>
      {label && <span style={lbl}>{label}</span>}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input style={inp} placeholder={ph ? `${ph} (EN)` : 'English'} value={v.en || ''} onChange={e => onChange({ ...v, en: e.target.value })} />
        <button type="button" title="Translate — fills the empty side (EN ⇄ TH)" onClick={translate} disabled={busy} style={{ ...btnG, padding: '6px 10px', fontSize: 13, flexShrink: 0 }}>{busy ? '…' : '⇄'}</button>
        <input style={{ ...inp, fontFamily: 'Sarabun, sans-serif' }} placeholder={ph ? `${ph} (ไทย)` : 'ไทย'} value={v.th || ''} onChange={e => onChange({ ...v, th: e.target.value })} />
      </div>
      {err && <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 3 }}>{err}</div>}
    </div>
  );
}

function Txt({ label, value, onChange, ph, type }: { label: string; value: string; onChange: (v: string) => void; ph?: string; type?: string }) {
  return <div><span style={lbl}>{label}</span><input style={inp} type={type || 'text'} placeholder={ph} value={value || ''} onChange={e => onChange(e.target.value)} /></div>;
}

// ── Shared field blocks (used by BOTH the New-POA form and the per-POA editor) ────────────────────
function ParamsFields({ txn, setTxn, date, setDate, jobId, setJobId, data, setData }: { txn: string; setTxn: (v: string) => void; date: string; setDate: (v: string) => void; jobId: string; setJobId: (v: string) => void; data: any; setData: (fn: (d: any) => any) => void }) {
  const setD = (p: string, v: any) => setData(d => deepSet(d, p, v));
  const setWit = (i: number, v: Bi) => setData(d => { const w = Array.isArray(d.witnesses) ? [...d.witnesses] : []; while (w.length < 2) w.push({ name: {} }); w[i] = { name: v }; return { ...d, witnesses: w }; });
  return (
    <div style={{ ...card, display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div><span style={lbl}>Transaction type</span><select style={inp} value={txn} onChange={e => setTxn(e.target.value)}>{TXN_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}</select></div>
        <Txt label="Date" type="date" value={date} onChange={setDate} />
      </div>
      <Txt label="Job ID" value={jobId} onChange={setJobId} />
      <BiField label="Made at" kind="address" value={data.madeAt || {}} onChange={v => setD('madeAt', v)} />
      <div style={hdr}>Lawyer (Donee)</div>
      <BiField label="Name" kind="name" value={data.donee?.name || {}} onChange={v => setD('donee.name', v)} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 14 }}>
        <Txt label="Age" value={data.donee?.age || ''} onChange={v => setD('donee.age', v)} />
        <Txt label="Thai ID No." value={data.donee?.idNo || ''} onChange={v => setD('donee.idNo', v)} />
      </div>
      <BiField label="Address" kind="address" value={data.donee?.addr || {}} onChange={v => setD('donee.addr', v)} />
      <div style={hdr}>Witnesses</div>
      {[0, 1].map(i => <BiField key={i} label={`Witness ${i + 1}`} kind="name" value={data.witnesses?.[i]?.name || {}} onChange={v => setWit(i, v)} />)}
      <div style={hdr}>Review</div>
      {REVIEW.map(([k, label]) => (
        <div key={k} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, alignItems: 'end' }}>
          <Txt label={label} value={data.review?.[k]?.by || ''} onChange={v => setD(`review.${k}.by`, v)} />
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, paddingBottom: 8 }}><input type="checkbox" checked={!!data.review?.[k]?.ok} onChange={e => setD(`review.${k}.ok`, e.target.checked)} /> Checked</label>
        </div>
      ))}
    </div>
  );
}

function PersonFields({ c, heading, onChange, onAddr, onUpload }: { c: any; heading: string; onChange: (patch: any) => void; onAddr: (k: string, v: any) => void; onUpload?: (f: File) => void }) {
  return (
    <div style={{ ...card, display: 'grid', gap: 12 }}>
      <div style={{ fontWeight: 700, color: 'var(--accent)' }}>{heading}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: 12 }}>
        <div><span style={lbl}>Title</span><select style={inp} value={c.title || ''} onChange={e => onChange({ title: e.target.value })}><option value="">—</option><option>Mr.</option><option>Ms.</option><option>Mrs.</option></select></div>
        <BiField label="First name" kind="name" value={c.first || {}} onChange={v => onChange({ first: v })} />
        <BiField label="Last name" kind="name" value={c.last || {}} onChange={v => onChange({ last: v })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Txt label="Age" value={c.age || ''} onChange={v => onChange({ age: v })} />
        <Txt label="Nationality" value={c.nationality || ''} onChange={v => onChange({ nationality: v })} />
        <Txt label="Email" value={c.email || ''} onChange={v => onChange({ email: v })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: onUpload ? '2fr 1fr' : '1fr', gap: 12, alignItems: 'end' }}>
        <Txt label="ID / Passport No." value={c.id_number || ''} onChange={v => onChange({ id_number: v })} />
        {onUpload && <label style={{ ...btnG, textAlign: 'center', cursor: 'pointer' }}>Upload ID{(c.id_file_ids?.length ? ` (${c.id_file_ids.length})` : '')}<input type="file" hidden onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); }} /></label>}
      </div>
      <span style={lbl}>Address</span>
      <BiField kind="address" value={c.addr?.line1 || {}} onChange={v => onAddr('line1', v)} ph="Address Line 1" />
      <BiField kind="address" value={c.addr?.line2 || {}} onChange={v => onAddr('line2', v)} ph="Address Line 2" />
      <BiField kind="address" value={c.addr?.city || {}} onChange={v => onAddr('city', v)} ph="City" />
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12 }}>
        <Txt label="" value={c.addr?.postal || ''} onChange={v => onAddr('postal', v)} ph="Postal / Zip" />
        <BiField kind="address" value={c.addr?.country || {}} onChange={v => onAddr('country', v)} ph="Country" />
      </div>
    </div>
  );
}

function CounterpartyFields({ data, setData, lease }: { data: any; setData: (fn: (d: any) => any) => void; lease: boolean }) {
  const setD = (p: string, v: any) => setData(d => deepSet(d, p, v));
  return (
    <div style={{ ...card, display: 'grid', gap: 14 }}>
      <div style={hdr}>{lease ? 'Lessor (Landlord)' : 'Seller'}</div>
      <div style={{ maxWidth: 220 }}><span style={lbl}>Type</span><select style={inp} value={data.counterparty?.kind || 'company'} onChange={e => setD('counterparty.kind', e.target.value)}><option value="individual">Individual</option><option value="company">Company</option></select></div>
      <BiField label="Name" kind="name" value={data.counterparty?.name || {}} onChange={v => setD('counterparty.name', v)} />
    </div>
  );
}

function PropertyFields({ data, setData, txn }: { data: any; setData: (fn: (d: any) => any) => void; txn: string }) {
  const setD = (p: string, v: any) => setData(d => deepSet(d, p, v));
  const obj = objOf(txn); const lease = isLease(txn);
  return (
    <div style={{ ...card, display: 'grid', gap: 14 }}>
      <div style={hdr}>Property &amp; Deal — {TXN_TYPES.find(t => t.key === txn)?.label}</div>
      {obj === 'condo' && <>
        <BiField label="Condominium name" kind="name" value={data.property?.condoName || {}} onChange={v => setD('property.condoName', v)} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Txt label="Building registration No." value={data.property?.buildingRegNo || ''} onChange={v => setD('property.buildingRegNo', v)} />
          <Txt label="Unit No." value={data.property?.unitNo || ''} onChange={v => setD('property.unitNo', v)} />
        </div>
      </>}
      {(obj === 'land' || obj === 'house') && <Txt label="Title deed (Chanote) No." value={data.property?.chanoteNo || ''} onChange={v => setD('property.chanoteNo', v)} />}
      {obj === 'house' && <Txt label="House registration No." value={data.property?.houseRegNo || ''} onChange={v => setD('property.houseRegNo', v)} />}
      <div style={{ maxWidth: 200 }}><Txt label="Moo" value={data.property?.moo || ''} onChange={v => setD('property.moo', v)} /></div>
      <BiField label="Sub-district" kind="address" value={data.property?.subdistrict || {}} onChange={v => setD('property.subdistrict', v)} />
      <BiField label="District" kind="address" value={data.property?.district || {}} onChange={v => setD('property.district', v)} />
      <BiField label="Province" kind="address" value={data.property?.province || {}} onChange={v => setD('property.province', v)} />
      <Txt label="Total price (Baht)" value={data.property?.price || ''} onChange={v => setD('property.price', v)} />
      {lease && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Txt label="Lease term (years)" value={data.property?.leaseYears || ''} onChange={v => setD('property.leaseYears', v)} />
        <Txt label="Lease start" type="date" value={data.property?.leaseStart || ''} onChange={v => setD('property.leaseStart', v)} />
        <Txt label="Lease end" type="date" value={data.property?.leaseEnd || ''} onChange={v => setD('property.leaseEnd', v)} />
      </div>}
    </div>
  );
}

function TabBar({ tabs, tab, setTab }: { tabs: string[]; tab: number; setTab: (i: number) => void }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
      {tabs.map((tb, i) => <button key={tb} onClick={() => setTab(i)} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, border: 'none', background: 'transparent', cursor: 'pointer', color: tab === i ? 'var(--accent)' : 'var(--text-tertiary)', borderBottom: `3px solid ${tab === i ? 'var(--accent)' : 'transparent'}` }}>{i + 1}. {tb}</button>)}
    </div>
  );
}

// Smart intake — paste / upload / drop a document; Claude reads the whole file and auto-fills the form.
// Shared by the New-POA form and the per-POA editor. getTarget() returns the current tab so an
// unlabelled person/company document is filed into whichever section the operator is on.
function useIntake(batchId: string, getTarget: () => string, onExtract: (data: any) => void) {
  const [intake, setIntake] = useState<{ busy: boolean; name: string; err: string; done: boolean }>({ busy: false, name: '', err: '', done: false });
  const [drag, setDrag] = useState(false);
  const [overlayHidden, setOverlayHidden] = useState(false);
  const handleFile = async (file: File) => {
    setOverlayHidden(false);
    setIntake({ busy: true, name: file.name || 'pasted image', err: '', done: false });
    // The AI call runs to completion and fills the form even if the user closes the waiting overlay.
    try { const r: any = await api.poaExtract(batchId, file, getTarget()); if (r?.data) onExtract(r.data); setIntake(s => ({ ...s, busy: false, done: true })); }
    catch (e: any) { setIntake(s => ({ ...s, busy: false, err: e?.message || 'Failed to parse the document' })); }
  };
  const cancelOverlay = () => { if (window.confirm('Cancelling only closes this waiting screen — the AI keeps analysing in the background and will fill the form when it finishes. If you start editing before it is done, the result may be incomplete or look incorrect. Close the waiting screen?')) setOverlayHidden(true); };
  const onPaste = (e: React.ClipboardEvent) => { const items: any = e.clipboardData?.items || []; for (let i = 0; i < items.length; i++) { const it = items[i]; if (it.type && it.type.startsWith('image/')) { const f = it.getAsFile(); if (f) { handleFile(f); e.preventDefault(); return; } } } };
  const dropProps = {
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); if (!drag) setDrag(true); },
    onDragLeave: (e: React.DragEvent) => { if (e.currentTarget === e.target) setDrag(false); },
    onDrop: (e: React.DragEvent) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer?.files?.[0]; if (f) handleFile(f); },
  };
  return { intake, drag, overlayHidden, onPaste, dropProps, handleFile, cancelOverlay };
}

// Centered full-screen "analysing…" animation that greys out and blocks the page while the AI reads
// the document. Cancel just closes it (the AI keeps running); the backdrop itself does NOT dismiss.
function IntakeOverlay({ show, name, onCancel }: { show: boolean; name: string; onCancel: () => void }) {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <style>{`@keyframes poaScan{0%{top:2%;opacity:0}12%{opacity:1}88%{opacity:1}100%{top:82%;opacity:0}}
.poa-scanwrap{position:relative;width:100px;height:100px;margin:0 auto;overflow:hidden;border-radius:12px}
.poa-scanline{position:absolute;left:6%;right:6%;height:8px;border-radius:6px;background:linear-gradient(90deg,transparent,#48c9ff,transparent);box-shadow:0 0 14px 2px #48c9ff;animation:poaScan 1.5s ease-in-out infinite}`}</style>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 14, padding: '30px 36px', textAlign: 'center', maxWidth: 400, boxShadow: '0 14px 44px rgba(0,0,0,.3)' }}>
        <div className="poa-scanwrap">
          <img className="app-logo" src="/logo.png" alt="SLF" style={{ width: 100, height: 100, objectFit: 'contain', display: 'block' }} />
          <div className="poa-scanline" />
        </div>
        <div style={{ fontSize: 15.5, fontWeight: 700, marginTop: 16, color: 'var(--text-primary)' }}>Analysing your document…</div>
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 6 }}>The AI is reading “{name}” and filling the form automatically. Please wait.</div>
        <button style={{ ...btnG, marginTop: 20 }} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function IntakeBar({ intake, onFile }: { intake: any; onFile: (f: File) => void }) {
  return (
    <div style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: 14, marginBottom: 14, background: 'var(--bg-surface)', textAlign: 'center', fontSize: 13 }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>📎 Auto-fill from a document — no typing needed</div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Upload a <b>PDF or image</b>, <b>drag a file</b> onto this page, or <b>paste a screenshot</b> (Ctrl / ⌘+V) — the system reads it and fills the whole form automatically.</span>
        <label style={{ ...btnG, cursor: 'pointer', padding: '6px 14px' }}>Upload file<input type="file" hidden accept="image/*,application/pdf" onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); (e.target as HTMLInputElement).value = ''; }} /></label>
      </div>
      {intake.err && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 6 }}>{intake.err}</div>}
      {intake.done && !intake.busy && <div style={{ color: 'var(--accent)', fontSize: 12.5, marginTop: 6 }}>✅ Filled from “{intake.name}” — please review each tab. Paste / drop more to add.</div>}
    </div>
  );
}

// ── Report list ──────────────────────────────────────────────────────────────
function List({ onOpen, onNew }: { onOpen: (id: string) => void; onNew: () => void }) {
  const [rows, setRows] = useState<any[]>([]);
  const [q, setQ] = useState(''); const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const d: any = await api.poaList(q, status, 1); setRows(d.rows || []); } catch { /* */ } setLoading(false); };
  useEffect(() => { const h = setTimeout(load, 220); return () => clearTimeout(h); }, [q, status]); // eslint-disable-line
  const del = async (e: React.MouseEvent, id: string) => { e.stopPropagation(); if (!window.confirm('Delete this POA? (soft delete — recoverable by an admin)')) return; try { await api.poaDelete(id); setRows(prev => prev.filter(r => r.id !== id)); } catch { /* */ } };
  return (
    <div style={{ padding: 16, overflow: 'auto', width: '100%', maxWidth: 892, alignSelf: 'center', boxSizing: 'border-box' }}>
      {/* One row for search + status, and "New POA" is the floating + every other list uses
          (owner 2026-09-25). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'nowrap' }}>
        <input style={{ ...inp, flex: 1, minWidth: 120 }} placeholder="Search POA no, client, Job ID…" value={q} onChange={e => setQ(e.target.value)} />
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, overflowX: 'auto' }}>
          {['', 'new', 'pending', 'confirmed', 'sent'].map(s => (
            <button key={s} onClick={() => setStatus(s)} style={{ ...btnG, whiteSpace: 'nowrap', background: status === s ? 'var(--accent)' : 'var(--bg-surface)', color: status === s ? '#fff' : 'var(--text-secondary)', borderColor: status === s ? 'var(--accent)' : 'var(--border)' }}>{s ? STATUS_LABEL[s] : 'All'}</button>
          ))}
        </div>
      </div>
      <Fab onClick={onNew} title="New POA" />
      {loading ? <div style={{ color: 'var(--text-tertiary)' }}>Loading…</div>
        : rows.length === 0 ? <div style={{ color: 'var(--text-tertiary)', padding: 24, textAlign: 'center' }}>No POAs yet.</div>
          : <div style={{ ...card, padding: 0 }}>{rows.map(r => (
            <div key={r.id} onClick={() => onOpen(r.id)} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
              <span style={{ color: 'var(--accent)', fontWeight: 700, minWidth: 92 }}>{r.report_no}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{r.client_name || '(untitled)'}</div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{(TXN_TYPES.find(t => t.key === r.txn_type)?.label || r.txn_type)} · Job {r.job_id || '—'} · {r.updated_by_name}</div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{STATUS_LABEL[r.status] || r.status}</span>
              <button title="Delete" onClick={e => del(e, r.id)} style={{ ...btnG, padding: '4px 9px', fontSize: 14, color: '#b91c1c', flexShrink: 0 }}>🗑</button>
            </div>
          ))}</div>}
    </div>
  );
}

// ── New-POA form (batch) ─────────────────────────────────────────────────────
const CREATE_TABS = ['Parameters', 'Clients', 'Counterparty', 'Property'];
function CreateForm({ batchId, onDone, onCancel }: { batchId: string; onDone: () => void; onCancel: () => void }) {
  const [tab, setTab] = useState(0);
  const [txn, setTxn] = useState('buy_condo');
  const [jobId, setJobId] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [data, setData] = useState<any>({ madeAt: DEFAULT_MADE_AT, donee: DEFAULT_DONEE, witnesses: DEFAULT_WITNESSES, review: defaultReview(), counterparty: { kind: 'company', name: {} }, property: {}, clients: [emptyClient()] });
  const [preview, setPreview] = useState<any>(null);
  const [msg, setMsg] = useState('');

  useEffect(() => { api.poaBatchGet(batchId).then((b: any) => { if (b.txn_type) setTxn(b.txn_type); if (b.job_id) setJobId(b.job_id); if (b.start_date) setStartDate(b.start_date); if (b.data && Object.keys(b.data).length) setData((d: any) => ({ ...d, ...b.data })); }).catch(() => {}); }, [batchId]);
  useEffect(() => { const h = setTimeout(() => { api.poaBatchPatch(batchId, { txn_type: txn, job_id: jobId, start_date: startDate, data }).catch(() => {}); }, 500); return () => clearTimeout(h); }, [txn, jobId, startDate, data]); // eslint-disable-line

  const setClient = (i: number, patch: any) => setData((d: any) => { const cl = [...(d.clients || [])]; cl[i] = { ...cl[i], ...patch }; return { ...d, clients: cl }; });
  const setClientAddr = (i: number, k: string, val: any) => setData((d: any) => { const cl = [...(d.clients || [])]; cl[i] = { ...cl[i], addr: { ...cl[i].addr, [k]: val } }; return { ...d, clients: cl }; });
  const setCount = (n: number) => setData((d: any) => { const cl = [...(d.clients || [])]; while (cl.length < n) cl.push(emptyClient()); cl.length = n; return { ...d, clients: cl }; });
  const uploadId = async (i: number, file: File) => { try { const r: any = await api.poaUpload(batchId, i, file); setClient(i, { id_file_ids: [...((data.clients[i].id_file_ids) || []), r.id] }); } catch (e: any) { setMsg(e.message); } };
  const doPreview = async () => { setMsg(''); try { const p: any = await api.poaPreview(batchId); setPreview(p); } catch (e: any) { setMsg(e.message); } };
  const doGenerate = async () => { try { await api.poaGenerate(batchId); onDone(); } catch (e: any) { setMsg(e.message); } };

  // Smart intake — the clients array auto-grows (capped at 5) to match how many people the document
  // contains, so the "Number of clients" selector follows the screenshot.
  const mergeExtract = (ex: any) => {
    if (ex.txn_type && TXN_TYPES.some(t => t.key === ex.txn_type)) setTxn(ex.txn_type);
    setData((d: any) => {
      const n = JSON.parse(JSON.stringify(d));
      if (Array.isArray(ex.clients) && ex.clients.length) {
        const take = ex.clients.slice(0, 5);
        const cl = Array.isArray(n.clients) ? [...n.clients] : [];
        while (cl.length < take.length) cl.push(emptyClient());
        take.forEach((exc: any, i: number) => { cl[i] = mergeFill(cl[i] || emptyClient(), exc); });
        n.clients = cl;
      }
      if (ex.counterparty) n.counterparty = mergeFill(n.counterparty || { kind: 'company', name: {} }, ex.counterparty);
      if (ex.property) n.property = mergeFill(n.property || {}, ex.property);
      return n;
    });
  };
  const iv = useIntake(batchId, () => ['parameters', 'clients', 'counterparty', 'property'][tab] || '', mergeExtract);

  const lease = isLease(txn);
  const clients: any[] = data.clients || [];
  // Leaving the create form throws away the draft, so confirm once the user has typed something.
  const isDirty = () => anyFilled(data.clients) || anyFilled(data.counterparty?.name) || anyFilled(data.property);
  const backToList = () => { if (isDirty() && !window.confirm('Discard this draft POA and go back to the list?')) return; onCancel(); };
  return (
    <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
      <IntakeOverlay show={iv.intake.busy && !iv.overlayHidden} name={iv.intake.name} onCancel={iv.cancelOverlay} />
      <div style={{ maxWidth: 860, margin: '0 auto' }} onPaste={iv.onPaste} {...iv.dropProps}>
        {/* Top bar mirrors the report editor so the way back to the list is visible without scrolling. */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <button style={btnG} onClick={backToList}>‹ POAs</button>
          <span style={{ fontSize: 16, fontWeight: 700 }}>New POA</span>
        </div>
        <IntakeBar intake={iv.intake} onFile={iv.handleFile} />
        <TabBar tabs={CREATE_TABS} tab={tab} setTab={setTab} />
        {tab === 0 && <ParamsFields txn={txn} setTxn={setTxn} date={startDate} setDate={setStartDate} jobId={jobId} setJobId={setJobId} data={data} setData={setData} />}
        {tab === 1 && <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ ...card, display: 'flex', gap: 14, alignItems: 'end' }}>
            <div style={{ maxWidth: 180 }}><span style={lbl}>Number of clients</span><select style={inp} value={clients.length} onChange={e => setCount(Number(e.target.value))}>{[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}</select></div>
            <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>Clients with the same last name AND address are merged into one POA.</div>
          </div>
          {clients.map((c, i) => <PersonFields key={i} heading={`Client ${i + 1}`} c={c} onChange={p => setClient(i, p)} onAddr={(k, v) => setClientAddr(i, k, v)} onUpload={f => uploadId(i, f)} />)}
        </div>}
        {tab === 2 && <CounterpartyFields data={data} setData={setData} lease={lease} />}
        {tab === 3 && <PropertyFields data={data} setData={setData} txn={txn} />}

        {msg && <div style={{ color: '#b91c1c', marginTop: 12 }}>{msg}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 18, alignItems: 'center' }}>
          <button style={btnG} onClick={backToList}>Cancel</button>
          {tab > 0 && <button style={btnG} onClick={() => setTab(tab - 1)}>‹ Previous</button>}
          {tab < CREATE_TABS.length - 1 && <button style={btnP} onClick={() => setTab(tab + 1)}>Next ›</button>}
          {tab === CREATE_TABS.length - 1 && <button style={btnP} onClick={doPreview}>Review → generate POAs</button>}
        </div>
      </div>

      {preview && (
        <div onClick={() => setPreview(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div onClick={e => e.stopPropagation()} style={{ ...card, width: 480, maxWidth: '90vw' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>This will create {preview.count} POA{preview.count === 1 ? '' : 's'}</div>
            <div style={{ display: 'grid', gap: 8, margin: '12px 0', maxHeight: '50vh', overflow: 'auto' }}>
              {(preview.groups || []).map((g: any, i: number) => (
                <div key={i} style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div style={{ fontWeight: 700 }}>POA {i + 1} · {g.members} grantor{g.members === 1 ? '' : 's'}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{g.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={btnG} onClick={() => setPreview(null)}>Back</button>
              <button style={btnP} onClick={doGenerate}>Generate {preview.count} POA{preview.count === 1 ? '' : 's'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Per-POA editor — SAME tabs/fields as New POA, plus a Document tab ─────────────────────────────
const EDIT_TABS = ['Parameters', 'Grantors', 'Counterparty', 'Property', 'Document'];
function ReportEditor({ id, onBack }: { id: string; onBack: () => void }) {
  const [r, setR] = useState<any>(null);
  const [data, setData] = useState<any>(null);
  const [txn, setTxn] = useState('buy_condo');
  const [jobId, setJobId] = useState('');
  const [tab, setTab] = useState(0);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState('');       // 'open' | 'download'
  const [preparing, setPreparing] = useState('');   // 'save' | 'send' — building the confirm dialog
  const [sending, setSending] = useState(false);    // dialog confirm in flight
  const [emailModal, setEmailModal] = useState<any>(null);
  const [docHtml, setDocHtml] = useState('');
  const fetchDoc = () => api.poaDocumentHtml(id).then((d: any) => setDocHtml(d?.renderable ? d.html : '')).catch(() => setDocHtml(''));
  const load = () => api.poaGet(id).then((x: any) => { setR(x); setData(x.data || {}); setTxn(x.txn_type || 'buy_condo'); setJobId(x.job_id || ''); fetchDoc(); }).catch(() => {});
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  // Smart intake — extracted people go into grantors (auto-growing, capped at 5). Defined BEFORE the
  // early return so the hook runs on every render (React rules-of-hooks).
  const mergeExtract = (ex: any) => {
    if (ex.txn_type && TXN_TYPES.some(t => t.key === ex.txn_type)) setTxn(ex.txn_type);
    setData((d: any) => {
      const n = JSON.parse(JSON.stringify(d));
      if (Array.isArray(ex.clients) && ex.clients.length) {
        const take = ex.clients.slice(0, 5);
        const g = Array.isArray(n.grantors) ? [...n.grantors] : [];
        while (g.length < take.length) g.push(emptyClient());
        take.forEach((exc: any, i: number) => { g[i] = mergeFill(g[i] || emptyClient(), exc); });
        n.grantors = g;
      }
      if (ex.counterparty) n.counterparty = mergeFill(n.counterparty || { kind: 'company', name: {} }, ex.counterparty);
      if (ex.property) n.property = mergeFill(n.property || {}, ex.property);
      return n;
    });
  };
  const iv = useIntake('', () => ['parameters', 'clients', 'counterparty', 'property', 'document'][tab] || '', mergeExtract);
  if (!r || !data) return <div style={{ padding: 24, color: 'var(--text-tertiary)' }}>Loading…</div>;
  const lease = isLease(txn);
  const grantors: any[] = data.grantors || [];
  const setGr = (i: number, patch: any) => setData((d: any) => { const g = [...(d.grantors || [])]; g[i] = { ...g[i], ...patch }; return { ...d, grantors: g }; });
  const setGrAddr = (i: number, k: string, val: any) => setData((d: any) => { const g = [...(d.grantors || [])]; g[i] = { ...g[i], addr: { ...g[i].addr, [k]: val } }; return { ...d, grantors: g }; });
  const setGrCount = (nn: number) => setData((d: any) => { const g = [...(d.grantors || [])]; while (g.length < nn) g.push(emptyClient()); g.length = nn; return { ...d, grantors: g }; });
  const persist = () => api.poaPatch(id, { data, txn_type: txn, job_id: jobId });
  const run = async (fn: () => Promise<any>, ok: string) => { setBusy(true); setMsg(''); try { await fn(); setMsg(ok); load(); } catch (e: any) { setMsg(e.message); } setBusy(false); };
  const saveChanges = () => run(persist, 'Changes saved.');
  // Save current edits, fetch the email preview, then open the confirm dialog (no send yet).
  const openEmail = async (mode: 'save' | 'send') => {
    setPreparing(mode); setMsg('');
    try { await persist(); const p: any = await api.poaEmailPreview(id, mode); setEmailModal({ ...p, mode }); }
    catch (e: any) { setMsg(e.message); }
    finally { setPreparing(''); }
  };
  const doEmailSend = async () => {
    const mode = emailModal?.mode; setSending(true); setMsg('');
    try {
      if (mode === 'save') await api.poaSave(id); else await api.poaSend(id);
      setEmailModal(null); setMsg(mode === 'save' ? 'Saved — internal staff notified.' : 'Sent to client + internal staff.'); load();
    } catch (e: any) { setMsg(e.message); }
    finally { setSending(false); }
  };
  const del = async () => { if (!window.confirm('Delete this POA? (soft delete — recoverable by an admin)')) return; try { await api.poaDelete(id); onBack(); } catch (e: any) { setMsg(e.message); } };

  return (
    <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
      <IntakeOverlay show={iv.intake.busy && !iv.overlayHidden} name={iv.intake.name} onCancel={iv.cancelOverlay} />
      <div style={{ maxWidth: 860, margin: '0 auto' }} onPaste={iv.onPaste} {...iv.dropProps}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <button style={btnG} onClick={onBack}>‹ POAs</button>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 16, fontWeight: 700 }}>{r.report_no}</span>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)' }}>{STATUS_LABEL[r.status] || r.status}</span>
            <button style={{ ...btnG, color: '#b91c1c' }} onClick={del}>Delete</button>
          </div>
        </div>
        <IntakeBar intake={iv.intake} onFile={iv.handleFile} />
        <TabBar tabs={EDIT_TABS} tab={tab} setTab={setTab} />
        {tab === 0 && <ParamsFields txn={txn} setTxn={setTxn} date={data.startDate || ''} setDate={v => setData((d: any) => deepSet(d, 'startDate', v))} jobId={jobId} setJobId={setJobId} data={data} setData={setData} />}
        {tab === 1 && <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ ...card, display: 'flex', gap: 14, alignItems: 'end' }}>
            <div style={{ maxWidth: 180 }}><span style={lbl}>Number of grantors</span><select style={inp} value={grantors.length} onChange={e => setGrCount(Number(e.target.value))}>{[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}</select></div>
            <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>Add or remove grantors on this POA.</div>
          </div>
          {grantors.map((c, i) => <PersonFields key={i} heading={`Grantor ${i + 1}`} c={c} onChange={p => setGr(i, p)} onAddr={(k, v) => setGrAddr(i, k, v)} />)}
        </div>}
        {tab === 2 && <CounterpartyFields data={data} setData={setData} lease={lease} />}
        {tab === 3 && <PropertyFields data={data} setData={setData} txn={txn} />}
        {tab === 4 && <div style={{ ...card, display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={hdr}>Document preview (Thai + English)</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...btnG, fontSize: 12.5 }} disabled={busy} onClick={() => run(persist, 'Saved — preview refreshed.')}>{busy ? '⏳ …' : 'Save & refresh'}</button>
              <button style={{ ...btnG, fontSize: 12.5 }} disabled={pdfBusy !== ''} onClick={async () => { setPdfBusy('open'); try { await persist(); await api.poaOpenPdf(id); } catch (e: any) { setMsg(e.message); } finally { setPdfBusy(''); } }}>{pdfBusy === 'open' ? '⏳ Generating…' : 'Open PDF'}</button>
              <button style={{ ...btnG, fontSize: 12.5 }} disabled={pdfBusy !== ''} onClick={async () => { setPdfBusy('download'); try { await persist(); await api.poaDownloadPdf(id, `${r.report_no || 'POA'}.pdf`); } catch (e: any) { setMsg(e.message); } finally { setPdfBusy(''); } }}>{pdfBusy === 'download' ? '⏳ Generating…' : 'Download PDF'}</button>
            </div>
          </div>
          {docHtml
            ? <iframe title="POA document" srcDoc={docHtml} style={{ width: '100%', height: 640, border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }} />
            : <div style={{ padding: '10px 12px', background: 'var(--bg-app)', border: '1px dashed var(--border)', borderRadius: 8, fontSize: 12.5, color: 'var(--text-tertiary)' }}>Click “Save &amp; refresh” to render the document.</div>}
        </div>}

        {msg && <div style={{ color: 'var(--accent)', fontSize: 13, marginTop: 12 }}>{msg}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <button style={btnG} onClick={saveChanges} disabled={busy}>{busy ? '⏳ Saving…' : 'Save changes'}</button>
          <button style={btnG} onClick={() => openEmail('save')} disabled={preparing !== ''}>{preparing === 'save' ? '⏳ Preparing…' : 'Confirm & notify internal'}</button>
          <button style={btnP} onClick={() => openEmail('send')} disabled={preparing !== ''}>{preparing === 'send' ? '⏳ Preparing…' : 'Send to client'}</button>
        </div>
      </div>

      {emailModal && (
        <div onClick={() => !sending && setEmailModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ ...card, width: 580, maxWidth: '95vw', maxHeight: '88vh', overflow: 'auto' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>{emailModal.mode === 'send' ? 'Send to client — please confirm' : 'Notify internal staff — please confirm'}</div>
            {emailModal.noClientEmail && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>⚠ No client email on this POA — add one in the Grantors tab first.</div>}
            <table style={{ fontSize: 13, marginBottom: 12, borderCollapse: 'collapse' }}><tbody>
              <tr><td style={emTd}>To</td><td style={{ padding: '3px 0' }}>{(emailModal.to || []).join(', ') || '—'}</td></tr>
              {emailModal.cc && emailModal.cc.length > 0 && <tr><td style={emTd}>Cc</td><td style={{ padding: '3px 0' }}>{emailModal.cc.join(', ')}</td></tr>}
              <tr><td style={emTd}>Subject</td><td style={{ padding: '3px 0' }}>{emailModal.subject}</td></tr>
              <tr><td style={emTd}>Attachment</td><td style={{ padding: '3px 0' }}>{emailModal.attachment ? `📎 ${emailModal.attachment}` : '—'}</td></tr>
            </tbody></table>
            <span style={lbl}>Message</span>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', background: 'var(--bg-app)', fontSize: 13.5 }} dangerouslySetInnerHTML={{ __html: emailModal.bodyHtml || '' }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={btnG} onClick={() => setEmailModal(null)} disabled={sending}>Cancel</button>
              <button style={btnP} onClick={doEmailSend} disabled={sending || emailModal.noClientEmail}>{sending ? '⏳ Sending…' : (emailModal.mode === 'send' ? 'Confirm & send to client' : 'Confirm & notify')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const emTd: React.CSSProperties = { fontWeight: 700, color: 'var(--text-secondary)', padding: '3px 12px 3px 0', verticalAlign: 'top', whiteSpace: 'nowrap' };

export default function Poa({ embedded }: { embedded?: boolean }) {
  useTranslation();
  const [view, setView] = useState<{ mode: 'list' } | { mode: 'create'; batchId: string } | { mode: 'report'; id: string }>({ mode: 'list' });
  const newPoa = async () => { try { const b: any = await api.poaBatchCreate('buy_condo'); setView({ mode: 'create', batchId: b.id }); } catch { /* */ } };
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-app)', height: embedded ? undefined : '100vh' }}>
      {view.mode === 'list' && <List onOpen={id => setView({ mode: 'report', id })} onNew={newPoa} />}
      {view.mode === 'create' && <CreateForm batchId={view.batchId} onDone={() => setView({ mode: 'list' })} onCancel={() => setView({ mode: 'list' })} />}
      {view.mode === 'report' && <ReportEditor id={view.id} onBack={() => setView({ mode: 'list' })} />}
    </div>
  );
}
